import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import throttle from 'lodash/throttle'
import { useConfig } from 'builder/hooks/useConfig'
import Preview from 'builder/components/Preview'
import Icon from 'builder/components/Icon'
import { DocumentTypes, FORMATS } from 'builder/modules/constants'
import { MouseClickEvent } from 'builder/modules/ui'
import { useStepByStepFeature } from 'builder/components/DocumentEditor/StepByStepFlow/hooks/useStepByStepFeature'
import { EditorPanelType } from 'builder/modules/generalEditor'
import { AiResumeReview } from 'builder/components/AiFeatures/AiResumeReview'
import { DownloadDocumentHandler, selectors } from 'builder/modules/resumeEditor'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { LOCAL_STORAGE_JS_CLICKED_CUSTOMIZE_MODAL_KEY } from 'builder/components/FindJob/SearchBar/constants'
import { useLocalStorage } from 'builder/hooks/useLocalStorage'
import templateHeightFactorSelector from '../Helper/utils/templateHeightFactorSelector'
import { TailoringFromPublicJobBoardPanelButton } from '../DocumentEditor/TailoringFromPublicJobBoard'
import { ResumeReviewPopup } from './components/ResumeReviewPopup'
import { CurrentDocument, PreviewRefProps } from './types'

import {
  Container,
  PreviewBox,
  PreviewWrapper,
  FullSizeOverlay,
  FullSizeIcon,
  FullWidthTopBarLayout,
} from './styles'
import TopbarLayout from './components/TopbarLayout'
import BottomBarLayout from './components/BottomBarLayout'
import { FloatingCustomizationButton } from './components/FloatingCustomizationButton/FloatingCustomizationButton'

type Props = {
  type: DocumentTypes
  currentDocument: CurrentDocument
  documentId: number
  isFullScreen?: boolean
  isDownloading: boolean
  onFullSize?: (event?: MouseClickEvent) => void
  showResumeDistributionModal?: (format: FORMATS) => void
  isOnline: boolean
  isSyncing: boolean
  isPrefilling?: boolean
  isAiResumeReviewFeatureActive?: boolean
  isEditorPanelTypeSwitcherEnabled?: boolean
  editorPanelType?: EditorPanelType
  handleEditorPanelTypeChange?: (type: EditorPanelType) => void
  top?: number
  handleDownloadDocument: DownloadDocumentHandler
  handleShareDocument?: () => void
  handleCombineDocuments?: () => void
  handleDownloadButtonMenuOpen?: () => void
  showAIHighlight?: boolean
  handleTurnOffAIHighlight?: (value: boolean) => void
}

const PreviewPanel = (props: Props): JSX.Element => {
  const [width, setWidth] = useState<number>(0)
  const [height, setHeight] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPagesNumber, setTotalPagesNumber] = useState<number>(1)
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true)
  const previewRef = useRef<PreviewRefProps>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const config = useConfig()
  const { isRbpStepFlowActive } = useStepByStepFeature()
  const [, setOpenTemplateView] = useLocalStorage<boolean>(
    LOCAL_STORAGE_JS_CLICKED_CUSTOMIZE_MODAL_KEY,
    false,
  )

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    selectors.autoTailoringFromPublicJobBoard,
  )

  const {
    type,
    currentDocument,
    documentId,
    isDownloading,
    isFullScreen,
    onFullSize,
    showResumeDistributionModal,
    isSyncing,
    isPrefilling,
    isOnline,
    isEditorPanelTypeSwitcherEnabled,
    handleEditorPanelTypeChange,
    editorPanelType,
    isAiResumeReviewFeatureActive,
    handleDownloadDocument,
    handleShareDocument,
    handleCombineDocuments,
    handleDownloadButtonMenuOpen,
    showAIHighlight,
    handleTurnOffAIHighlight,
  } = props

  let stagingResumeIoHost = window.location.hostname === 'staging.resume.io'
  const isResumeIo = !config?.features.superApp || stagingResumeIoHost
  const shouldShowSelectTemplateButton = editorPanelType !== 'ai_review'

  const shouldShowFloatingCustomizationButton =
    editorPanelType !== 'ai_review' &&
    isAiResumeReviewFeatureActive &&
    !shouldShowSelectTemplateButton

  const handlePreviewResize = throttle(() => {
    if (!containerRef.current) return
    const pdfHeightFactor = templateHeightFactorSelector(currentDocument)
    const previewHeight = containerRef.current.offsetHeight
    const previewWidth = containerRef.current.offsetWidth

    const width = Math.min(previewWidth, previewHeight * pdfHeightFactor)
    setWidth(Math.round(width))

    setHeight(Math.round(width / pdfHeightFactor))

    if (!previewRef.current) return
    previewRef.current.resize(width)
  }, 50)

  useEffect(() => {
    window.addEventListener('resize', handlePreviewResize)
    handlePreviewResize()
    return () => {
      window.removeEventListener('resize', handlePreviewResize)
    }
  }, [handlePreviewResize])

  useEffect(() => {
    if (previewRef.current) previewRef.current.reset()
  }, [isFullScreen])

  useEffect(() => {
    const onHover = () => {
      setIsCollapsed(false)
    }

    const onLeave = () => {
      setIsCollapsed(true)
    }

    const container = containerRef.current

    container?.addEventListener('mouseenter', onHover)
    container?.addEventListener('mouseleave', onLeave)

    return () => {
      container?.removeEventListener('mouseenter', onHover)
      container?.removeEventListener('mouseleave', onLeave)
    }
  }, [isFullScreen])

  const handlePaginationStateChange = useCallback(
    (state: { currentPage: number; totalPagesCount: number }) => {
      const { currentPage, totalPagesCount } = state
      setCurrentPage(currentPage + 1)
      setTotalPagesNumber(totalPagesCount)
    },
    [],
  )

  const handleFullSize = useCallback(
    (event?: MouseClickEvent) => {
      onFullSize?.(event)
      trackInternalEvent(`open_full_screen_${type}_preview`)
      setOpenTemplateView(true)
    },
    [onFullSize, type, setOpenTemplateView],
  )

  const handleOpenFullSize = useCallback(
    (event: MouseClickEvent) => {
      trackInternalEvent(`click_${type}_fullscreen_button`)
      handleFullSize(event)
    },
    [handleFullSize, type],
  )

  return (
    <Container top={props.top} id="preview-panel-container">
      {isEditorPanelTypeSwitcherEnabled && (
        <FullWidthTopBarLayout
          {...{
            type,
            documentId,
            isDownloading,
            currentDocument,
            handleFullSize,
            showResumeDistributionModal,
            isRbpStepFlowActive,
            isEditorPanelTypeSwitcherEnabled,
            handleEditorPanelTypeChange,
            editorPanelType,
            isAiResumeReviewFeatureActive,
            shouldShowSelectTemplateButton,
            handleShareDocument,
            handleDownloadDocument,
            handleCombineDocuments,
            handleDownloadButtonMenuOpen,
            showAIHighlight,
            handleTurnOffAIHighlight,
          }}
        />
      )}
      {editorPanelType === 'ai_review' ? (
        <AiResumeReview />
      ) : (
        <PreviewBox
          $isAutoTailoringFromPublicJobBoard={autoTailoringFromPublicJobBoard.enabled}
          ref={containerRef}
        >
          <PreviewWrapper data-testid="preview-panel-preview-wrapper">
            <FullSizeOverlay onClick={handleOpenFullSize}>
              {isOnline && !shouldShowFloatingCustomizationButton && (
                <FullSizeIcon>
                  <Icon.FullSizeLarge />
                </FullSizeIcon>
              )}
              {autoTailoringFromPublicJobBoard.enabled && (
                <TailoringFromPublicJobBoardPanelButton />
              )}
            </FullSizeOverlay>
            {!autoTailoringFromPublicJobBoard.enabled && !isEditorPanelTypeSwitcherEnabled && (
              <TopbarLayout
                {...{
                  width,
                  type,
                  documentId,
                  isDownloading,
                  currentDocument,
                  handleFullSize,
                  showResumeDistributionModal,
                  isRbpStepFlowActive,
                  shouldShowSelectTemplateButton,
                  handleDownloadDocument,
                  handleShareDocument,
                  handleCombineDocuments,
                  handleDownloadButtonMenuOpen,
                  showAIHighlight,
                  handleTurnOffAIHighlight,
                }}
              />
            )}
            {!!width && (
              <Preview
                {...{
                  height,
                  width,
                  documentId,
                  type,
                  document: currentDocument,
                  ref: previewRef,
                  onPaginationChange: handlePaginationStateChange,
                }}
              />
            )}
            <BottomBarLayout
              {...{
                type,
                currentDocument,
                currentPage,
                totalPagesNumber,
                documentId,
                previewRef,
                isOnline,
                isPrefilling,
                isSyncing,
              }}
            />
            {shouldShowFloatingCustomizationButton && (
              <FloatingCustomizationButton isCollapsed={isCollapsed} />
            )}
          </PreviewWrapper>
        </PreviewBox>
      )}
      {type === DocumentTypes.resume && isResumeIo && <ResumeReviewPopup resumeId={documentId} />}
    </Container>
  )
}

export default memo(PreviewPanel)
